import { getCharsCountForService } from '@buffer-mono/char-toolkit'
import type { CharCountService } from '@buffer-mono/char-toolkit'

type MediaRequirement = {
  channel: string
  media: string
}

const MEDIA_REQUIRED_BY_SERVICE: Record<string, MediaRequirement> = {
  instagram: { channel: 'Instagram', media: 'an image or video' },
  pinterest: { channel: 'Pinterest', media: 'an image or video' },
  tiktok: { channel: 'TikTok', media: 'an image or video' },
  youtube: { channel: 'YouTube', media: 'a video' },
}

// Post types where the requirement differs from the service default: a Reel
// only accepts a video, and a regular Facebook post needs no media at all
// (facebook deliberately has no MEDIA_REQUIRED_BY_SERVICE entry).
const MEDIA_REQUIRED_BY_POST_TYPE: Record<
  string,
  Record<string, MediaRequirement>
> = {
  facebook: {
    reel: { channel: 'Facebook', media: 'a video' },
    story: { channel: 'Facebook', media: 'an image or video' },
  },
  instagram: {
    reel: { channel: 'Instagram', media: 'a video' },
  },
}

function getMediaRequirement(
  service: string,
  postType: string | null | undefined,
  relaxed: boolean,
): MediaRequirement | null {
  // `relax-draft-validation` off: guard Instagram only, ignoring post type.
  if (!relaxed) {
    return service === 'instagram' ? MEDIA_REQUIRED_BY_SERVICE.instagram : null
  }

  const postTypeRequirement = postType
    ? MEDIA_REQUIRED_BY_POST_TYPE[service]?.[postType]
    : undefined

  return postTypeRequirement ?? MEDIA_REQUIRED_BY_SERVICE[service] ?? null
}

/**
 * A channel that requires media to publish still accepts a text-only draft —
 * the requirement only blocks turning that draft into a real post, so this
 * only flags a post that already has text and is still missing its media.
 * Covered channels and media: see the requirement maps above.
 */
export const getMissingRequiredMediaMessage = ({
  service,
  postType,
  assets,
  text,
  relaxed,
}: {
  service: string
  postType?: string | null
  assets: unknown[] | null | undefined
  text: string | null
  /** `relax-draft-validation` flag state — off keeps the Instagram-only guard. */
  relaxed: boolean
}): string | null => {
  const requirement = getMediaRequirement(service, postType, relaxed)
  if (!requirement) {
    return null
  }

  const hasMedia = Boolean(assets && assets.length > 0)
  if (hasMedia) {
    return null
  }

  const hasText = Boolean(text && text.trim().length > 0)
  if (!hasText) {
    return null
  }

  return `Please include ${requirement.media} for ${requirement.channel}`
}

/**
 * Flags a post whose text is over the channel's character limit, so the UI can
 * block publishing it rather than let it fail against the network when it goes
 * out. CORE-7611 relaxed the character-limit check on draft *save* for orgs on
 * `relax-draft-validation`, which lets an over-limit draft exist; this guards
 * the publish/queue transition the same way getMissingRequiredMediaMessage
 * guards missing media. Counting uses `getCharsCountForService`, the same
 * per-service counter the backend's charLimitRule uses, so the count matches
 * each platform's own rules (X weighted characters, Bluesky graphemes, etc.)
 * and the guard predicts what the service will accept. `charLimit` is resolved
 * by the caller from the channel's service and subscription tier; a nullish
 * limit means "no limit to enforce" and never flags.
 *
 * For a threaded post the root text is the first thread item (thread[0]), so a
 * thread is checked item-by-item and the root text is not re-counted.
 */
export const getOverCharacterLimitMessage = ({
  service,
  serviceName,
  text,
  thread,
  charLimit,
}: {
  /** char-toolkit service key; selects the platform's counting rules. */
  service: CharCountService
  serviceName: string
  text: string | null
  thread?: ReadonlyArray<{ text: string }> | null
  charLimit: number | null | undefined
}): string | null => {
  if (!charLimit) {
    return null
  }

  const isOverLimit = (value: string): boolean =>
    getCharsCountForService(value, service) > charLimit

  if (thread && thread.length > 0) {
    const overLimitIndex = thread.findIndex((item) =>
      isOverLimit(item.text ?? ''),
    )
    if (overLimitIndex === -1) {
      return null
    }
    return thread.length > 1
      ? `Thread post ${overLimitIndex + 1} exceeds the ${charLimit} character limit`
      : `${serviceName} posts cannot exceed ${charLimit} characters`
  }

  if (text && isOverLimit(text)) {
    return `${serviceName} posts cannot exceed ${charLimit} characters`
  }

  return null
}
